<template>
  <div>
    <div v-if="showForm" class="modal-backdrop fade in"></div>
    <div v-if="showForm" class="modal fade in" style="display: block;">
      <div class="modal-dialog">
        <div class="modal-content">
          <SubgroupForm
            :subgroup="editingSubgroup"
            @save="handleSave"
            @cancel="closeForm"
          ></SubgroupForm>
        </div>
      </div>
    </div>
    <div v-if="showRules" class="modal-backdrop fade in"></div>
    <div v-if="showRules" class="modal fade in" style="display: block;">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <a class="close" @click="closeRulesManager">&times;</a>
            <h4 class="modal-title">Manage Rules for {{ selectedSubgroup.name }}</h4>
          </div>
          <div class="modal-body">
            <SubgroupRules
              :subgroup="selectedSubgroup"
              :domain-uuid="domainUuid"
              @close="closeRulesManager"
            ></SubgroupRules>
          </div>
        </div>
      </div>
    </div>
    <div class="page-header">
      <div class="pull-right">
        <button class="btn btn-primary" @click="showAddForm" :disabled="!canManageSubgroups">Add Subgroup</button>
      </div>
      <h2>Subgroups</h2>
    </div>

    <div v-if="loading" class="text-center">
      <i class="fa fa-spinner fa-spin fa-2x"></i>
    </div>

    <div v-if="!loading && subgroups.length === 0">
      <p>No subgroups have been created yet.</p>
    </div>

    <div class="alert alert-warning" v-if="errorMessage">
      {{ errorMessage }}
    </div>

    <div v-if="!loading && subgroups.length > 0">
      <div class="form-group">
        <input type="text" class="form-control" placeholder="Filter by name..." v-model="filterText">
      </div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th @click="sortByName" style="cursor: pointer;">
              Name
              <i class="fa" :class="{'fa-sort-asc': sortBy === 'name' && sortDir === 'asc', 'fa-sort-desc': sortBy === 'name' && sortDir === 'desc'}"></i>
            </th>
            <th @click="sortByType" style="cursor: pointer;">
              Type
              <i class="fa" :class="{'fa-sort-asc': sortBy === 'type' && sortDir === 'asc', 'fa-sort-desc': sortBy === 'type' && sortDir === 'desc'}"></i>
            </th>
            <th @click="sortByEnabledRules" style="cursor: pointer;">
              Enabled Rules
              <i class="fa" :class="{'fa-sort-asc': sortBy === 'enabled_rules_count' && sortDir === 'asc', 'fa-sort-desc': sortBy === 'enabled_rules_count' && sortDir === 'desc'}"></i>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="subgroup in filteredSubgroups" :key="subgroup.uuid">
            <td>{{ subgroup.name }} <span v-if="subgroup.user_count" class="small">({{ subgroup.user_count }} users)</span></td>
            <td>{{ subgroup.type || '-' }}</td>
            <td>{{ subgroup.enabled_rules_count || 0 }}</td>
            <td>
              <button class="btn btn-xs btn-default" @click="showEditForm(subgroup)" :disabled="!canManageSubgroups">Edit</button>
              <button class="btn btn-xs btn-info" @click="showRulesManager(subgroup)" :disabled="!canManageSubgroups">Manage Rules</button>
              <button class="btn btn-xs btn-danger" @click="showDeleteConfirm(subgroup)" :disabled="!canManageSubgroups">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import SubgroupForm from './SubgroupForm.vue';
import SubgroupRules from './SubgroupRules.vue';
import { Permissions } from '@/client_auth/permissions';

export default {
  components: {
    SubgroupForm,
    SubgroupRules,
  },
  name: 'Subgroups',
  props: {
    domainUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      subgroups: [],
      filterText: '',
      sortBy: 'name',
      sortDir: 'asc',
      showForm: false,
      editingSubgroup: null,
      showRules: false,
      selectedSubgroup: null,
      errorMessage: '',
      canManageSubgroups: false,
    };
  },
  computed: {
    filteredSubgroups() {
      let filtered = this.subgroups;
      if (this.filterText) {
        const lowerFilter = this.filterText.toLowerCase();
        filtered = filtered.filter(s => s.name.toLowerCase().includes(lowerFilter));
      }

      if (this.sortBy) {
        filtered.sort((a, b) => {
          let aVal = a[this.sortBy];
          let bVal = b[this.sortBy];
          
          // Handle null/undefined values
          if (aVal == null) aVal = this.sortBy === 'type' ? '' : 0;
          if (bVal == null) bVal = this.sortBy === 'type' ? '' : 0;
          
          // Handle number sorting for enabled_rules_count
          if (this.sortBy === 'enabled_rules_count') {
            if (aVal < bVal) return this.sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return this.sortDir === 'asc' ? 1 : -1;
            return 0;
          }
          
          // Handle string sorting for name and type
          const aStr = String(aVal || '').toLowerCase();
          const bStr = String(bVal || '').toLowerCase();
          if (aStr < bStr) return this.sortDir === 'asc' ? -1 : 1;
          if (aStr > bStr) return this.sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return filtered;
    },
  },
  methods: {
    async fetchSubgroups() {
      this.errorMessage = '';
      this.loading = true;
      try {
        const { data } = await this.$angularWorld.adminEntitiesApi.listSubgroups({ domain_uuid: this.domainUuid, include_user_count: true });
        this.subgroups = data;
      } catch (error) {
        console.error('Failed to fetch subgroups', error);
        this.errorMessage = 'Failed to fetch subgroups';
      } finally {
        this.loading = false;
      }
    },
    showAddForm() {
      this.editingSubgroup = null;
      this.showForm = true;
    },
    showEditForm(subgroup) {
      this.editingSubgroup = { ...subgroup };
      this.showForm = true;
    },
    async showDeleteConfirm(subgroup) {
      this.errorMessage = '';
      // eslint-disable-next-line no-alert
      if (window.confirm(`Are you sure you want to delete "${subgroup.name}"?`)) {
        try {
          await this.$angularWorld.adminEntitiesApi.deleteSubgroup(subgroup.uuid);
          await this.fetchSubgroups();
        } catch (error) {
          console.error('Failed to delete subgroup', error);
          this.errorMessage = 'Failed to delete subgroup';
        }
      }
    },
    closeForm() {
      this.showForm = false;
      this.editingSubgroup = null;
    },

    async handleSave(subgroup) {
      this.errorMessage = '';
      try {
        if (subgroup.uuid) {
          await this.$angularWorld.adminEntitiesApi.updateSubgroup(subgroup);
        } else {
          await this.$angularWorld.adminEntitiesApi.createSubgroup({ ...subgroup, domain_uuid: this.domainUuid });
        }
        this.closeForm();
        await this.fetchSubgroups();
      } catch (error) {
        console.error('Failed to save subgroup', error);
        this.errorMessage = 'Failed to save subgroup';
        this.closeForm();
      }
    },

    sortByName() {
      if (this.sortBy === 'name') {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = 'name';
        this.sortDir = 'asc';
      }
    },

    sortByType() {
      if (this.sortBy === 'type') {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = 'type';
        this.sortDir = 'asc';
      }
    },
    
    sortByEnabledRules() {
      if (this.sortBy === 'enabled_rules_count') {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = 'enabled_rules_count';
        this.sortDir = 'asc';
      }
    },

    showRulesManager(subgroup) {
      this.selectedSubgroup = subgroup;
      this.showRules = true;
    },

    closeRulesManager() {
      this.showRules = false;
      this.selectedSubgroup = null;
    },

    async updatePermissions() {
      await this.$angularWorld.auth
        .doesUserHavePermission([Permissions.UserOps])
        .then(hasPermission => {
          this.canManageSubgroups = hasPermission
        })
    }
  },
  async mounted() {
    await this.fetchSubgroups()
    await this.updatePermissions()
  },
  watch: {
    '$angularWorld.auth.currentUser'() {
      this.updatePermissions();
    },
  },
};
</script>
<style scoped>
.modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>